import { jsx, jsxs } from "react/jsx-runtime";
import { Stack, Box } from "braid-design-system";
import { useState, useRef, useEffect } from "react";
import { ResponsiveContainer, BarChart, CartesianGrid, Tooltip, Legend, Bar, XAxis, YAxis } from "recharts";
import { StackBarChartTick } from "./StackBarChartTick.mjs";
import { barChart } from "./StackBarChart.css.mjs";
const sumForData = (data) => Object.values(data).reduce((total, value) => total + (value ?? 0), 0);
const calculateTicksForYAxis = (items) => {
  const max = Math.max(...items.map((item) => sumForData(item.data)));
  const ticks = [];
  const numOfTicks = 4;
  const tickIntervals = max / numOfTicks;
  ticks.push(0);
  for (let i = 1; i < numOfTicks; i++) {
    ticks.push(Math.ceil(tickIntervals * i));
  }
  ticks.push(max);
  return ticks;
};
const numberFormatter = (locale, style = "decimal") => new Intl.NumberFormat(locale, {
  maximumFractionDigits: 0,
  notation: "standard",
  style
});
const StackBarChart = ({
  items,
  dataMap,
  locale,
  height = 410,
  isNVLEnabled,
  overrides = {
    xAxisTickGap: 0,
    axisLines: { x: true, y: true },
    tickLines: { x: true, y: true },
    strokeDashLines: false,
    barSize: 100,
    margin: {
      top: 10,
      left: 0,
      right: 0,
      bottom: 0
    },
    percentageValues: { x: false, y: false }
  },
  curvedBars = false,
  tooltipContent,
  legendContent
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const [yAxisWidth, setYAxisWidth] = useState(0);
  const chartRef = useRef(null);
  useEffect(() => {
    if (chartRef.current) {
      setTimeout(() => {
        var _a2, _b2;
        const newYAxisWidth = ((_b2 = (_a2 = chartRef.current) == null ? void 0 : _a2.querySelector(".recharts-cartesian-axis.recharts-yAxis")) == null ? void 0 : _b2.getBoundingClientRect().width) || 0;
        setYAxisWidth(newYAxisWidth);
      }, 100);
    }
  }, [setYAxisWidth]);
  const data = items.map((item) => ({
    name: item.name,
    ...item.data
  }));
  return /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(Box, { style: { height: `${height}px` }, ref: chartRef, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
    BarChart,
    {
      data,
      className: data.length > 90 ? "" : barChart,
      margin: {
        top: (_a = overrides.margin) == null ? void 0 : _a.top,
        left: (_b = overrides.margin) == null ? void 0 : _b.left,
        right: (_c = overrides.margin) == null ? void 0 : _c.right,
        bottom: (_d = overrides.margin) == null ? void 0 : _d.bottom
      },
      barSize: overrides.barSize,
      children: [
        /* @__PURE__ */ jsx(
          CartesianGrid,
          {
            strokeDasharray: overrides.strokeDashLines ? "4 4" : "0",
            horizontal: overrides.strokeDashLines,
            vertical: false
          }
        ),
        tooltipContent ? /* @__PURE__ */ jsx(
          Tooltip,
          {
            cursor: { fill: "#EAECF1" },
            content: (props) => tooltipContent(props, items)
          }
        ) : null,
        legendContent ? /* @__PURE__ */ jsx(
          Legend,
          {
            content: (props) => legendContent(props),
            align: "left",
            verticalAlign: isNVLEnabled ? "top" : "bottom"
          }
        ) : null,
        Object.entries(dataMap).map(([key, { color, label }]) => /* @__PURE__ */ jsx(
          Bar,
          {
            name: label,
            dataKey: key,
            stackId: "a",
            fill: color,
            radius: curvedBars ? [10, 10, 0, 0] : 0
          },
          key
        )),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            dataKey: "name",
            tick: (props) => /* @__PURE__ */ jsx(
              StackBarChartTick,
              {
                axis: "x",
                axisProps: props,
                isNVLEnabled
              }
            ),
            tickMargin: 4,
            interval: "preserveStart",
            minTickGap: overrides.xAxisTickGap || 15,
            stroke: "#d2d7df",
            axisLine: (_e = overrides.axisLines) == null ? void 0 : _e.x,
            tickLine: (_f = overrides.tickLines) == null ? void 0 : _f.x
          }
        ),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            interval: "preserveEnd",
            width: yAxisWidth > 0 ? yAxisWidth : void 0,
            ticks: calculateTicksForYAxis(items),
            tickFormatter: (value) => {
              var _a2;
              return ((_a2 = overrides.percentageValues) == null ? void 0 : _a2.y) ? numberFormatter(locale, "percent").format(
                value / 100
              ) : numberFormatter(locale, "decimal").format(value);
            },
            tick: (props) => /* @__PURE__ */ jsx(
              StackBarChartTick,
              {
                axis: "y",
                axisProps: props,
                isNVLEnabled
              }
            ),
            tickMargin: 4,
            domain: [0, "dataMax"],
            allowDecimals: false,
            stroke: "#d2d7df",
            axisLine: (_g = overrides.axisLines) == null ? void 0 : _g.y,
            tickLine: (_h = overrides.tickLines) == null ? void 0 : _h.y
          }
        )
      ]
    }
  ) }) }) });
};
export {
  StackBarChart
};
